import { Injectable } from '@angular/core';
import { BrowserConfig, throwAsyncError } from 'webcommon/shared';

@Injectable({
  providedIn: 'root',
})
export class BrowserService {
  // Properties can be defined here for what browser, version, etc should be intentionally private.
  // Users of this service should ask for features, not type/version of a browser.

  // Because of the browsers defined in browserslist, and angular dropping support for IE,
  // we don't need to detect some of this stuff anymore.

  constructor(
  ) {
    // noop
  }

  getConfig(): BrowserConfig {
    const navigatorObj = this.getNavigator();
    const windowObj = this.getWindow();
    const userAgent = navigatorObj.userAgent;

    // I updated this iOS logic in Oct 2020 based on these answers on SO:
    // https://stackoverflow.com/a/58979271/5335355
    // https://stackoverflow.com/a/58065241/5335355
    // TODO: we need to eventually refactor this logic and dependent logic, since navigator.platform is deprecated
    /* eslint-disable deprecation/deprecation  */
    const isIphone = /iPhone|iPod/.test(navigatorObj.platform) &&
      !(windowObj as any).MSStream;
    const isIpad = (/iPad/.test(navigatorObj.platform) ||
      (navigatorObj.platform === 'MacIntel' && navigatorObj.maxTouchPoints > 2)) &&
      !(windowObj as any).MSStream;
    const isIosOS = isIphone || isIpad;
    /* eslint-enable deprecation/deprecation  */

    // android detection mentioned here
    // http://googlewebmastercentral.blogspot.com/2011/03/mo-better-to-also-detect-mobile-user.html
    const isAndroidOS = (/android/im).test(userAgent);
    const isAndroidPhone = isAndroidOS && (/mobile/im).test(userAgent);
    const isAndroidTablet = isAndroidOS && !isAndroidPhone;

    const isPhone = isIphone || isAndroidPhone;
    const isTablet = isIpad || isAndroidTablet;

    const isMobileDevice = isPhone || isTablet;

    const settings: BrowserConfig = {
      isAndroidOS,
      isIosOS,
      isMobileDevice,
      isPhone,
      isTablet,
    };
    return settings;
  }

  // TODO: come up with some unit tests to confirm what cases each implementation is better:
  // base64ToBlob (array copy) vs base64ToBlobAsync (fetch)
  // i think this implementation was from https://gist.github.com/fupslot/5015897
  // another one that we used in apBase64.js that is slightly different is
  // https://stackoverflow.com/a/16245768/5335355
  base64ToBlob(base64String: string, contentType: string): Blob {
    const windowObj = this.getWindow();

    const byteCharacters = windowObj.atob(base64String);
    const byteArrayBuffer = new ArrayBuffer(byteCharacters.length);
    const byteArrayInt = new Uint8Array(byteArrayBuffer);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrayInt[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([byteArrayInt], {type: contentType});
  }

  // https://stackoverflow.com/a/36183085/5335355
  async base64ToBlobAsync(base64String: string, contentType: string): Promise<Blob> {
    return await this.dataUrlToBlobAsync(`data:${contentType};base64,${base64String}`);
  }

  async dataUrlToBlobAsync(input: RequestInfo | URL): Promise<Blob> {
    const res = await fetch(input);
    return res.blob();
  }

  downloadDocumentToFile(fileNameToSaveAs: string, base64String: string, contentType: string) {
    const blob = this.base64ToBlob(base64String, contentType);
    this.saveBlob(document, blob, fileNameToSaveAs);
  }

  tryDownloadDocumentToFile(fileNameToSaveAs: string, base64String: string, contentType: string): boolean {
    if (!this.supportsDownloadToFile()) {
      return false;
    }

    this.downloadDocumentToFile(fileNameToSaveAs, base64String, contentType);
    return true;
  }

  getDefaultSpinnerName(): string {
    // this originially returned 'android' for IE
    return 'bubbles';
  }

  getDocument(): Document {
    return document;
  }

  getLocation(): Location {
    return location;
  }

  getNavigator(): Navigator {
    return navigator;
  }

  getWindow(): Window & typeof globalThis {
    return window;
  }

  // allows this to be mocked in tests, idk maybe there's a better way to do this
  reload(): void {
    location.reload();
  }

  isBlobCompatible(): boolean {
    // this was originally only false for older versions of IE, I think <10
    return true;
  }

  // TODO: PRM is the only consumer right now, and it doesn't have to deal with permissions.
  // The error handling probably needs to change once this is in a browser.
  queryForMicrophones() {
    const navigatorObj = this.getNavigator();
    return navigatorObj.mediaDevices.getUserMedia({ audio: true }).then(
      mediaStream => {
        return navigatorObj.mediaDevices.enumerateDevices().catch(deviceFail => {
          console.error('enumerateDevices', deviceFail);
          return Promise.reject(deviceFail);
        }).finally(() => {
          mediaStream.getTracks().forEach(track => {
            track.stop();
          });
        });
      },
      rejectReason => {
        console.error('getUserMedia', rejectReason);
        return Promise.reject(rejectReason);
      },
    );
  }

  // if user hasn't already given permission for microphone, this will return useless info
  queryForMicrophonesBestEffort() {
    const navigatorObj = this.getNavigator();
    return navigatorObj.mediaDevices.enumerateDevices();
  }

  saveBlob(document: Document, blobObj: Blob, fileName: string): void {
    const downloadLink = document.createElement('a');
    downloadLink.download = fileName;
    const objectUrl = URL.createObjectURL(blobObj);
    downloadLink.href = objectUrl;
    downloadLink.onclick = (e: MouseEvent) => {
      document.body.removeChild(e.target as Node);
    };
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    // We should want to call URL.revokeObjectURL() on this objectUrl when it's done downloading,
    // or when the objectUrl is safe to clean up,
    // but it's not clear when that will be.
    // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL#memory_management
    // Theoretically, the download could take a while.
    // Even FileSaver just uses an arbitrary time:
    // https://github.com/eligrey/FileSaver.js/issues/205
    // https://github.com/eligrey/FileSaver.js/commit/62d219a0fac54b94cd4f230e7bfc55aa3f8dcfa4
    // https://github.com/eligrey/FileSaver.js/commit/485930a#commitcomment-8768047
    // current code for FileSaver:
    // https://github.com/eligrey/FileSaver.js/blob/master/src/FileSaver.js#L105-L107
  }

  saveDataUri(document: Document, dataUri: string, fileName: string): void {
    const downloadLink = document.createElement('a');
    downloadLink.href = dataUri;
    downloadLink.target = '_blank';
    downloadLink.download = fileName;
    downloadLink.onclick = (e: MouseEvent) => {
      document.body.removeChild(e.target as Node);
    };
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  shouldAlwaysIgnoreCache(): boolean {
    // this was originally only true for IE
    return false;
  }

  // separating this logic to a different method will allow us to expand this logic if necessary
  // without having to call isBlobCompatible() before calling downloadDocumentToFile()
  supportsDownloadToFile(): boolean {
    const supported = this.isBlobCompatible();
    return supported;
  }

  // ported from old BrowserService.js
  supportsInlinePdf(): boolean {
    // this was originally only false for IE and Legacy Edge (before Chromium)
    return true;
  }

  // currently this appears to be used to just get the height and width
  // of the Image, but it's possible it could be used for other things
  loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => {
        resolve(i);
      };
      i.onerror = (e) => {
        reject(e);
      };
      i.src = src;
    });
  }

  blobToBase64(data: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          const base64dataUrl = reader.result.toString();

          // Note: The blob's result cannot be directly decoded as Base64 without first
          // removing the Data-URL declaration preceding the Base64-encoded data.
          // To retrieve only the Base64 encoded string, first remove data:/;base64, from the result.
          const base64data = base64dataUrl.substring(base64dataUrl.indexOf(',') + 1);

          resolve(base64data);
        } else {
          reject(new Error('undefined result from FileReader'));
        }
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsDataURL(data);
    });
  }

  /**
   * Checks if a given DOM element is visible in the current viewport.
   *
   * @see https://www.30secondsofcode.org/js/s/element-is-visible-in-viewport
   *
   * @param el - The HTML element to check visibility for.
   * @param partiallyVisible - If `true`, returns `true` when any part of the element is visible (overlaps viewport).
   *                           If `false`, returns `true` only when the entire element is fully within the viewport bounds.
   * @returns `true` if the element is visible in the viewport per `partiallyVisible` flag; otherwise, `false`.
   *
   * @example
   * const isFullyVisible = this.elementIsVisibleInViewport(myElement);
   * const isPartiallyVisible = this.elementIsVisibleInViewport(myElement, true);
   */
  public elementIsVisibleInViewport(el?: HTMLElement | null, partiallyVisible: boolean = false): boolean {
    if (!el) {
      return false;
    }

    const rect = el.getBoundingClientRect();
    const { top, left, bottom, right } = rect;
    const { innerHeight, innerWidth } = this.getWindow();

    if (partiallyVisible) {
      return (
        top < innerHeight &&
        bottom > 0 &&
        left < innerWidth &&
        right > 0
      );
    }

    return (
      top >= 0 &&
      left >= 0 &&
      bottom <= innerHeight &&
      right <= innerWidth
    );
  }

  /**
   * Checks if a scrollable element has been scrolled to the bottom.
   *
   * @param element - The HTML element to check scroll position for.
   * @returns `true` if the element is scrolled to the bottom; otherwise, `false`.
   *
   * @example
   * const isAtBottom = this.isScrolledToBottom(scrollContainer);
   */
  public isScrolledToBottom(element: HTMLElement): boolean {
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;
    return scrollPosition >= scrollHeight;
  }
}

// this method is instead of URL.parse, since it's not available, and doesn't seem worth to polyfill
// https://developer.mozilla.org/en-US/docs/Web/API/URL/parse_static
// The logic is based off of this link ^
// and https://github.com/zloirock/core-js/blob/4b7201bb18a66481d8aa7ca28782c151bc99f152/packages/core-js/modules/web.url.parse.js
export function apParseUrl(urlString: string, base?: string): URL | null {
  try {
    return new URL(urlString, base);
  } catch (error) {
    // throwAsyncError, because I don't want to actually break at this point, but still bubble an error
    // Maybe take this out in the future if it's causing problems
    throwAsyncError(error);
    return null;
  }
}
